import React, { useCallback } from 'react';
import { Editor } from '@tiptap/react';
import { asRect } from '../../maths/geometry/rect';
import type { Rect } from '../../maths/geometry/rect/rectTypes';
import type { PlatformDetails } from '../../platform/platformTypes';
import { getEditorFocusIndex } from '../utils/tiptapEditorFocusPositionUtils';

interface UseElementTiptapEditorClickHandlerProps {
    isSingleSelected: boolean;
    editor: Editor;
    isEditingThisEditor: boolean;
    isEditable: boolean;
    editorId: string;
    editorKey: string;
    platformDetails: PlatformDetails;
    startEditing?: (args: { editorId: string; editorKey: string; editorFocusRect: Rect }) => void;
}

/**
 * Triggers the startEditing function when the editor is clicked & editable.
 * Also finds the caret position based on the click position and focuses the editor at that position.
 */
const useElementTiptapEditorClickHandler = ({
    isSingleSelected,
    editor,
    isEditingThisEditor,
    isEditable,
    editorId,
    editorKey,
    platformDetails,
    startEditing,
}: UseElementTiptapEditorClickHandlerProps) =>
    useCallback(
        (event: React.MouseEvent) => {
            if (!isEditable) {
                // Let the click bubble so read-only elements can still be selected.
                return;
            }

            if (!startEditing) return;
            if (!isSingleSelected) return;
            if (isEditingThisEditor) return;

            const caretIndex = getEditorFocusIndex(editor, event.nativeEvent, platformDetails);
            const editorFocusRect = asRect(editor.view.coordsAtPos(caretIndex));

            // Pass in editorFocusRect to startEditing, to make sure app scrolls properly
            // to the editor focus position. See mobileEditingMiddleware.ts.
            startEditing({ editorId, editorKey, editorFocusRect });

            editor.commands.focus(caretIndex);
        },
        [isSingleSelected, editor, isEditingThisEditor, isEditable, editorId, editorKey, platformDetails],
    );

export default useElementTiptapEditorClickHandler;
